import { GlobeAltIcon, HomeIcon, XIcon } from '@heroicons/react/outline';
import { Modal } from 'antd';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import { countryAtom } from '@/atoms/countryState';

interface Props {
  open: boolean;
  onClose: () => void;
  billingCountry?: string;
  globalCountry?: string;
  onScrollToBilling?: () => void;
}

const CountryMismatchModal: React.FC<Props> = ({
  open,
  onClose,
  billingCountry,
  globalCountry,
  onScrollToBilling,
}) => {
  const [, setCountryState] = useRecoilState(countryAtom);

  const [currentBillingCountry, setCurrentBillingCountry] =
    useState(billingCountry);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (open && !hasInitialized) {
      setCurrentBillingCountry(billingCountry);
      setHasInitialized(true);
    }

    if (!open && hasInitialized) {
      setHasInitialized(false);
    }
  }, [open, billingCountry, hasInitialized]);
  const handleGlobalChange = () => {
    setCountryState((prev) => ({
      ...prev,
      isOpen: true,
      globalCountryChange: true,
    }));
    onClose();
  };

  const handleScrollToBilling = () => {
    onClose();
    setTimeout(() => {
      if (onScrollToBilling) {
        onScrollToBilling();
      } else {
        const section = document.getElementById('billing-address');
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 350);
  };

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      closable={false}
      width={400}
      classNames={{
        body: 'bg-transparent',
        content: 'bg-transparent shadow-none',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className='relative bg-[#0f0f0f]/90 backdrop-blur-xl border border-gray-800 rounded-2xl shadow-2xl px-6 py-7 text-center text-white'
      >
        <button
          onClick={onClose}
          className='absolute top-3 right-3 text-gray-400 hover:text-gray-200 transition-colors'
          aria-label='Close'
        >
          <XIcon className='w-5 h-5' />
        </button>

        <div className='mb-6'>
          <h2 className='text-lg sm:text-xl font-semibold mb-3'>
            Country Mismatch Detected 🌍
          </h2>
          <p className='text-gray-400 text-sm sm:text-[15px] leading-relaxed max-w-xs mx-auto'>
            Your billing country{' '}
            <span className='text-primary font-semibold'>
              {currentBillingCountry || 'N/A'}
            </span>{' '}
            doesn’t match your global country{' '}
            <span className='text-primary font-semibold'>
              {globalCountry || 'N/A'}
            </span>
            .
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.02, backgroundColor: '#1c1c1c' }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGlobalChange}
          className='w-full flex items-center gap-3 bg-[#141414]/90 border border-gray-800 hover:border-primary/60 transition-all rounded-xl px-4 py-3.5 text-left mb-4'
        >
          <div className='flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/10'>
            <GlobeAltIcon className='w-5 h-5 text-primary' />
          </div>
          <div className='flex-1'>
            <p className='text-[15px] font-medium text-white'>
              Change Global Country
            </p>
            <p className='text-xs text-gray-400 mt-0.5'>
              Update your global country to match your billing info.
            </p>
          </div>
        </motion.button>

        <div className='flex items-center justify-center my-4'>
          <div className='flex-1 h-px bg-gray-700' />
          <span className='text-gray-500 text-xs px-2'>or</span>
          <div className='flex-1 h-px bg-gray-700' />
        </div>

        <motion.button
          whileHover={{ scale: 1.02, backgroundColor: '#1c1c1c' }}
          whileTap={{ scale: 0.98 }}
          onClick={handleScrollToBilling}
          className='w-full flex items-center gap-3 bg-[#141414]/90 border border-gray-800 hover:border-primary/60 transition-all rounded-xl px-4 py-3.5 text-left'
        >
          <div className='flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/10'>
            <HomeIcon className='w-5 h-5 text-primary' />
          </div>
          <div className='flex-1'>
            <p className='text-[15px] font-medium text-white'>
              Update Billing Address
            </p>
            <p className='text-xs text-gray-400 mt-0.5'>
              Scroll to your billing section to update your address.
            </p>
          </div>
        </motion.button>
      </motion.div>
    </Modal>
  );
};

export default CountryMismatchModal;
